import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import cx from "classnames";
import styles from "./Notification.module.scss";

const DEFAULT_TIMEOUT = 5000;
export const NOTIFICATION_PORTAL_ID = "phasersNotificationDestination";

interface NotificationDestinationProps {
  className?: string;
}

export const NotificationDestination: React.FC<NotificationDestinationProps> = ({ className }) => {
  return <div id={NOTIFICATION_PORTAL_ID} className={cx(styles.destination, className)} />;
};

interface NotificationPortalProps {
  id?: string;
}

export const NotificationPortal: React.FC<NotificationPortalProps> = ({ children, id }) => {
  const el = document.getElementById(id || NOTIFICATION_PORTAL_ID);
  return <>{el ? createPortal(children, el) : children}</>;
};

export interface NotificationProps {
  className?: string;
  theme?: "neutral" | "success" | "danger";
  portalId?: string;
  onDismiss?: () => void;
  autoDismiss?: boolean | number;
}

const Notification: React.FC<NotificationProps> = ({
  className,
  children,
  theme = "neutral",
  portalId,
  onDismiss,
  autoDismiss,
}) => {
  useEffect(() => {
    let timeout = 0;

    if (typeof autoDismiss === "number") {
      timeout = autoDismiss;
    } else if (autoDismiss) {
      timeout = DEFAULT_TIMEOUT;
    }

    const timer = setTimeout(() => {
      if (autoDismiss && onDismiss) {
        onDismiss();
      }
    }, timeout);

    return () => {
      clearTimeout(timer);
    };
  }, [autoDismiss, onDismiss]);

  return (
    <NotificationPortal id={portalId}>
      <div
        className={cx(styles.notification, className, {
          [styles.neutral]: theme === "neutral",
          [styles.success]: theme === "success",
          [styles.danger]: theme === "danger",
        })}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div className={styles.body}>
          {typeof children === "string" ? <p>{children}</p> : children}
        </div>
        {onDismiss && (
          <button
            type="button"
            className={styles.dismissButton}
            aria-label="Dismiss Notification"
            onClick={onDismiss}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        )}
      </div>
    </NotificationPortal>
  );
};

export default Notification;
